import spartaEventsUtility from "utilities/global/sparta-events-utility/2.3.3/js/sparta-events-utility";
import spartaGlobalFunctions from "utilities/global/sparta-global-functions-utility/2.1.2/js/sparta-global-functions-utility";
import jQuery from "utilities/vendor/jquery/3.5.1/js/jquery";
import $ from "utilities/vendor/jquery/3.5.1/js/jquery";
import DataAssetLoader from "utilities/suits/data-asset-loader/2.1.0/js/data-asset-loader";
import spartaTransportUtility from "utilities/global/sparta-transport-utility/4.0.2/js/sparta-transport-utility";
/* global DataAssetLoader spartaEventsUtility spartaGlobalFunctionsUtility */

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * @name        Validation Rules Importer
 * @file        validation-rules-importer.js
 * @description Imports the appropriate external JSON file for a given rule from the data folder
 * @classdesc   Imports the appropriate external JSON file for a given rule from the data folder
 * @global      Creates a a global ValidationRulesImporter object
 * @author      James A. Black Jr.
 * @copyright   © 2017 Bank of America Corporation. All rights reserved.
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/**
 * @const utilityNamespace
 * @description namespace for Validation Rules Importer.
 */
const utilityNamespace = 'spartaValidationFramework';

/**
 * @const EVENTS
 * @description constant for storing namespaced event strings
 */
const EVENTS = {
  RULES_IMPORT_COMPLETE_EVENT: `RULES_IMPORT_COMPLETE.${utilityNamespace}`,
  RULES_IMPORT_SUCCESS_EVENT: `RULES_IMPORT_SUCCESS.${utilityNamespace}`,
  RULES_IMPORT_ERROR_EVENT: `RULES_IMPORT_ERROR.${utilityNamespace}`,
};

/**
 * @const RULES
 * @description constant for storing validation rules lists object
 */
const RULES = {
  'AML.addressLine1': {
    filename: 'invalid-address-list.json',
  },
  'AML.addressLine2': {
    filename: 'invalid-address-list.json',
  },
  'AML.businessName': {
    filename: 'invalid-business-list.json',
  },
  'AML.cityCounty': {
    filename: 'invalid-city-list.json',
  },
  'AML.countryCode': {
    filename: 'country-code-list.json',
  },
  'AML.firstName': {
    filename: 'invalid-firstname-list.json',
  },
  'AML.lastName': {
    filename: 'invalid-lastname-list.json',
  },
  'AML.middleName': {
    filename: 'invalid-middlename-list.json',
  },
  'AML.nameGeneration': {
    filename: 'name-generation-list.json',
  },
  'AML.namePrefix': {
    filename: 'name-prefix-list.json',
  },
  'AML.nameSuffix': {
    filename: 'name-suffix-list.json',
  },
  'AML.stateCode': {
    filename: 'state-code-list.json',
  },
  pending: [],
  complete: [],
};

/**
 * ValidationRulesImporter for SpartaValidationFramework
 */
class ValidationRulesImporter {
  /**
   * Starts sparta subs
   */
  constructor() {
    spartaEventsUtility.sub(this.EVENTS.RULES_IMPORT_SUCCESS_EVENT, (rule) => {
      this.completedRulesEvent(rule.data);
    }, true);
  }

  /**
   * Initializiation method.
   * @returns {Object} Rules object.
   */
  init() {
    return this.RULES;
  }

  /**
   * @property {Object} EVENTS() exposes the events list constant
   * @description getter for EVENTS constant
   * @returns {Object} EVENTS
   */
  get EVENTS() { // eslint-disable-line class-methods-use-this
    return EVENTS;
  }

  /**
   * @property {Object} RULES() exposes validation rules filenames list constant
   * @description getter for RULES constant
   * @returns {Object} RULES
   */
  get RULES() { // eslint-disable-line class-methods-use-this
    return RULES;
  }

  /**
   * @method getValidationRules
   * @description Retrieves JSON files associated with a rule from the data folder.
   * @param {string} rule - rule name to retrieve the associated filename from the RULES object
   * @returns {void}
   */
  getValidationRules(rule) {
    this.language = spartaGlobalFunctions.getLanguage();
    
    const { filename } = this.RULES[rule];

    // generate a new DataAssetLoader instance
    const ruleAssetLoader = new DataAssetLoader('global/sparta-validation-framework', `data/${this.language}`);

    // load the relevant rule JSON file, which triggers success and failure events
    // so that the utility can subscribe to them
    const load = ruleAssetLoader.loadAsset(filename);

    spartaEventsUtility.sub(load.success, (evtData) => {
      // trigger the original ajaxSuccess event
      this.ajaxSuccess(evtData.data.xhr, rule);
    }, true);

    spartaEventsUtility.sub(load.failure, (evtData) => {
      // log out an error for debugging the issue
      this.ajaxError(evtData.data.xhr, rule);
    }, true);
  }

  /**
   * @method getAllPendingRules
   * @description Retrieves all pending validation rules.
   * @returns {void}
   */
  getAllPendingRules() {
    this.RULES.pending.forEach((rule) => {
      const isCompleted = this.RULES.complete.includes(rule);

      if (!isCompleted) {
        this.getValidationRules(rule);
      }
    });
  }

  /**
   * @method ajaxSuccess
   * @description Adds the retrieved list to the rule.
   * @param {object} data - the data returned by the jQuery $.ajax() / Sparta Transport method
   * @param {string} rule - name of the rule to assign the data to it's list property
   * @returns {void}
   */
  ajaxSuccess(data, rule) {
    this.RULES[rule].list = data;

    spartaEventsUtility.pub(this.EVENTS.RULES_IMPORT_SUCCESS_EVENT, `${rule}`);
  }

  /**
   * @method ajaxError
   * @description Sets the list property for the rule to an empty array.
   * @param {object} xhr - the xhr object returned by the jQuery $.ajax() / Sparta Transport method
   * @param {string} rule - name of the rule to assign the empty array to it's list property
   * @returns {void}
   */
  ajaxError(xhr, rule) {
    this.RULES[rule].list = [];

    spartaEventsUtility.pub(this.EVENTS.RULES_IMPORT_ERROR_EVENT, { xhr, rule });

    if (window.console && console.warn) {
      console.warn(`Sparta Validation: ${rule} AJAX Error.`, xhr);
    }
  }

  /**
   * @method completedRulesEvent
   * @description Checks to see if all rules have been imported, triggers completed EVENT.
   * @param {string} rule - rule name add to complete list
   * @returns {void}
   */
  completedRulesEvent(rule) {
    let newRullAdded = false;

    // Checks the pending array and complete array for the new rule.
    // If a pending rule is not in the complete array then it is added
    if (this.RULES.pending.includes(rule) && !this.RULES.complete.includes(rule)) {
      this.RULES.complete.push(rule);
      newRullAdded = true;
    }

    // If pending and complete are the same size and every pending element is in the complete array
    // then allRulesComplete is true.
    this.allRulesComplete = (this.RULES.pending.length === this.RULES.complete.length) &&
      this.RULES.pending.every((element) => {
        const isCompleted = this.RULES.complete.includes(element);

        return isCompleted;
      });

    // newRullAdded flag will prevent the pub from firing multiple times if a rule has already been
    // added.
    if (this.allRulesComplete && newRullAdded) {
      spartaEventsUtility.pub(this.EVENTS.RULES_IMPORT_COMPLETE_EVENT, this.RULES.complete);
    }
  }
}

const validationRulesImporter = new ValidationRulesImporter();

export default validationRulesImporter;
